import React, { ReactNode, useCallback, useContext, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { DataErrorModalProps } from 'ia-react-core/~/components/modal/DataErrorModal';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import { ParametersExceptFirst } from 'ia-react-core/~/interfaces/ParametersExceptFirst';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import DataErrorModal from '../../modal/DataErrorModal';
import ResourceMapper, { ResourceMapperProps } from '../_ResourceMapper';
import Saga from '../../redux/Saga';
import Mapper from '../../redux/Mapper';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';

import resourceActionSaga from './ResourceAction.saga';
import SchemaContext from '../../schema/_SchemaContext/SchemaContext';

export interface ResourceActionRenderProps {
  resourceId: number;
  pending: boolean;
  error: AxiosResponse;
  attempts: number;
  maxAttempts: number;
  requestResource(): void;
}

export interface ResourceActionProps<Data, Error = {}> extends Omit<ResourceMapperProps<Data, Error>, 'children'>, Omit<DataErrorModalProps, 'children' | 'error' | 'pending' | 'attempts' | 'onRetry'> {
  /** A function that will receive `resourceId`, `pending`, `error`, `attempts`, `maxAttempts` and the `requestResource` action function. */
  children?: (renderProps: ResourceActionRenderProps) => ReactNode;
  /** @ignore */
  method?: Method;
}

interface ResourceActionInternalProps {
  resourceActionId: number;
}

export interface ResourceActionComposedProps<Data, Error = {}> extends ResourceActionProps<Data, Error>, ResourceActionInternalProps {

}

const ResourceActionDefaultProps = {
  method: 'POST' as Method,
};

/**
 * @since 0.21.0
 */
const ResourceAction = Object.assign(
  <Data, Error = {}>({ children, resourceActionId, onRequest, ...props }: ResourceActionComposedProps<Data, Error>) => {
    const dispatch = useDispatch();
    const dispatchRequestResourceById = useCallback((...args: Parameters<typeof requestResourceById>) => dispatch(requestResourceById(...args)), [dispatch]);
    const { schema } = useContext(SchemaContext);

    const lastConfigRef = useRef<AxiosRequestConfig>({});

    const handleRequest = useCallback((config: AxiosRequestConfig) => {
      const newConfig = onRequest ? onRequest(config) : config;
      lastConfigRef.current = newConfig as AxiosRequestConfig;
      return newConfig;
    }, [onRequest]);

    return (
      <ResourceMapper<Data, Error>
        effect="sequence"
        {...filterPropsByComponent(props, ResourceMapper<Data, Error>)}
        onRequest={handleRequest}
      >
        {({ resourceId, name, pending, error, attempts, maxAttempts }) => (
          <>
            <Mapper<{ modalId: number }> src={`${schema.decorateComponent('modal', { name }).name}ErrorModal${resourceActionId}.modalId`}>
              {({ modalId }) => modalId ? (
                <Saga<{ resourceId: number; modalId: number }>
                  saga={resourceActionSaga}
                  resourceId={resourceId}
                  modalId={modalId}
                />
              ) : null}
            </Mapper>
            <DataErrorModal
              {...filterPropsByComponent(props, DataErrorModal)}
              name={`${name}ErrorModal${resourceActionId}`}
              onRetry={() => { dispatchRequestResourceById(resourceId, lastConfigRef.current.data, lastConfigRef.current); }}
              pending={pending}
              error={error}
              attempts={attempts}
              maxAttempts={maxAttempts}
            />
            {
              children ?
                children({
                  resourceId,
                  pending,
                  error,
                  attempts,
                  maxAttempts,
                  requestResource: (...args: ParametersExceptFirst<typeof requestResourceById>) => (
                    dispatchRequestResourceById(resourceId, ...args)
                  ),
                }) :
                null
            }
          </>
        )}
      </ResourceMapper>
    );
  },
  {
    defaultProps: ResourceActionDefaultProps,
    publicProps: [...ResourceMapper.publicProps, ...DataErrorModal.publicProps] as StringKeyOf<ResourceActionProps<any>>[],
  },
);

export default ResourceAction;
